import { useEffect, useState } from 'react';

import { useSyncTimezone, useUpdatePatient } from '@/lib/api/hooks';

/**
 * Auto-syncs the patient's stored timezone with their browser TZ on portal
 * load, and shows a 6-second revertable toast when the value actually moves.
 *
 * Why this exists: before the timezone-bug fix, every patient with no
 * Healthie timezone got silently painted "America/New_York", which made
 * appointment-time rendering wrong for anyone outside ET (and was the
 * root cause of "patient hops on the call but the doc isn't there" misses).
 * Now we capture the browser TZ on every portal load — quietly when it
 * matches, with a revertable toast when it doesn't.
 *
 * Fire-once-per-tab: we set a sessionStorage flag so a chatty patient
 * navigating between screens doesn't see the toast repeatedly. If they
 * close the tab and re-open, we sync again — fine, the server short-circuits
 * when the value is already correct.
 */
const STORAGE_FLAG = 'tz_sync_ran';

export function TimezoneSyncToast() {
  const syncMut = useSyncTimezone();
  const updateMut = useUpdatePatient();
  const [toast, setToast] = useState<{
    previous: string | null;
    current: string;
  } | null>(null);

  useEffect(() => {
    // Dev-only preview: `?tz_preview=<previous>:<current>` short-circuits
    // the real sync and just renders the toast with those values. Lets us
    // eyeball the UI without seeding fixtures or wiring a login. Stripped
    // in production builds by the import.meta.env.DEV guard.
    if (import.meta.env.DEV) {
      const url = new URL(window.location.href);
      const preview = url.searchParams.get('tz_preview');
      if (preview) {
        const [previous, current] = preview.split(':');
        if (current) {
          setToast({
            previous: previous && previous !== 'null' ? previous : null,
            current,
          });
          return;
        }
      }
    }
    if (sessionStorage.getItem(STORAGE_FLAG)) return;
    let tz: string | null = null;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      tz = null;
    }
    if (!tz) return;
    sessionStorage.setItem(STORAGE_FLAG, '1');
    syncMut.mutate(tz, {
      onSuccess: (data) => {
        // First-capture (previous=null) is silent — a brand-new patient
        // who never set a timezone shouldn't see a "your timezone changed"
        // toast on their first portal load; that read like a bug. Real
        // changes (previous=X, current=Y) still surface the revertable
        // toast so a VPN/travel switch doesn't silently move them.
        if (data.changed && data.previous) {
          setToast({ previous: data.previous, current: data.current });
        }
      },
      onError: () => {
        // Auto-sync is best-effort — never block the portal on a flaky
        // backend. We'll try again next load.
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  useEffect(() => {
    if (!toast) return;
    // Keep the dev preview pinned so the designer can read/screenshot
    // without it self-dismissing. Production toasts auto-dismiss at 6s.
    if (import.meta.env.DEV && new URL(window.location.href).searchParams.get('tz_preview')) {
      return;
    }
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  if (!toast) return null;

  const onRevert = () => {
    // Only meaningful when there was a previous value to restore. If
    // previous was null (first capture), revert dismisses the toast
    // without an update — the patient can still set it manually in /account.
    if (toast.previous) {
      updateMut.mutate({ timezone: toast.previous });
    }
    setToast(null);
  };

  const label = toast.current.replace(/_/g, ' ');
  return (
    <div
      role="status"
      className="fixed bottom-24 right-24 z-[100] type-label-sm px-14 py-10 rounded-lg shadow-lg border bg-ink text-page border-ink flex items-center gap-12"
    >
      <span>Timezone updated to {label}. Was this you?</span>
      {toast.previous && (
        <button
          type="button"
          onClick={onRevert}
          className="underline underline-offset-2"
        >
          Revert
        </button>
      )}
      <button
        type="button"
        onClick={() => setToast(null)}
        className="opacity-70 hover:opacity-100"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
